import { queryOptions, useQuery } from '@tanstack/react-query';

import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

import { useOrganizations } from '@cast/shared-organization-context-data-access';

const NODE_STORAGE_STATE_QUERY_KEY = 'node-storage-state';

const getNodeStorageStateQueryKey = (params: {
  organizationId: string | undefined;
  clusterId: string | undefined;
  nodeName: string | undefined;
}) =>
  [
    NODE_STORAGE_STATE_QUERY_KEY,
    params.organizationId,
    params.clusterId,
    params.nodeName,
  ] as const;

type NodeStorageStateQueryParams = {
  organizationId: string | undefined;
  clusterId: string | undefined;
  nodeName: string | undefined;
  enabled?: boolean;
};

const getNodeStorageStateQueryOptions = ({
  organizationId,
  clusterId,
  nodeName,
  enabled = true,
}: NodeStorageStateQueryParams) =>
  queryOptions({
    queryKey: getNodeStorageStateQueryKey({
      organizationId,
      clusterId,
      nodeName,
    }),
    queryFn: async () => {
      const { data } = await apiClient.stop.getNodeStorageSummary({
        organizationId: organizationId!,
        clusterId: clusterId!,
        nodeName: nodeName!,
      });

      return data;
    },
    enabled: makeIsEnabled(!!organizationId, !!clusterId, !!nodeName, enabled),
  });

type UseNodeStorageStateQueryArgs = {
  clusterId?: string;
  nodeName?: string;
  enabled?: boolean;
};

export const useNodeStorageStateQuery = ({
  clusterId,
  nodeName,
  enabled,
}: UseNodeStorageStateQueryArgs) => {
  const { currentOrganization } = useOrganizations();

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    getNodeStorageStateQueryOptions({
      organizationId: currentOrganization?.id,
      clusterId,
      nodeName,
      enabled,
    })
  );

  return {
    storageState: data?.capabilities,
    isLoading: isLoading || isFetching,
    error,
    refetch,
  };
};
